// Branch — the administration design: the branches on the left, the one you
// picked on the right. Picking a row is what used to be the row's checkbox, so
// Settings still works on the branch you chose and still opens the existing
// BranchSetting dialog with the same fetch behind it.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import BranchSetting from "../../components/adminsitration/BranchSetting";
import { fetchBranchSettings } from "../../redux/settingbyBranch";
import { fetchfinancialyear, fetchStateList, fetchType } from "../../redux/userSetting";
import { setheadertoggle } from "../../redux/toggle";
import {
  AdminScreen,
  AdminList,
  AxAvatar,
  AxEmptyDetail,
  AxField,
  AxPill,
  AxRefreshIcon,
  AxSectionHead,
  AxStatusKeys,
} from "../../components/adminsitration/ui";

const SORTS = [{ key: "name", label: "Branch" }];

const SettingsIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <circle cx="8" cy="8" r="2.2" />
    <path d="M13 9.4a1.2 1.2 0 0 0 .24 1.32l.05.04a1.45 1.45 0 1 1-2.05 2.05l-.04-.05a1.2 1.2 0 0 0-1.32-.24 1.2 1.2 0 0 0-.73 1.1v.13a1.45 1.45 0 1 1-2.9 0v-.07a1.2 1.2 0 0 0-.78-1.1 1.2 1.2 0 0 0-1.32.24l-.04.05a1.45 1.45 0 1 1-2.05-2.05l.05-.04a1.2 1.2 0 0 0 .24-1.32 1.2 1.2 0 0 0-1.1-.73H1.1a1.45 1.45 0 1 1 0-2.9h.07a1.2 1.2 0 0 0 1.1-.78 1.2 1.2 0 0 0-.24-1.32l-.05-.04A1.45 1.45 0 1 1 4.03 1.7l.04.05a1.2 1.2 0 0 0 1.32.24h.06a1.2 1.2 0 0 0 .73-1.1V.76a1.45 1.45 0 1 1 2.9 0v.07a1.2 1.2 0 0 0 .73 1.1 1.2 1.2 0 0 0 1.32-.24l.04-.05a1.45 1.45 0 1 1 2.05 2.05l-.05.04a1.2 1.2 0 0 0-.24 1.32v.06a1.2 1.2 0 0 0 1.1.73h.13a1.45 1.45 0 1 1 0 2.9h-.07a1.2 1.2 0 0 0-1.1.73z" />
  </svg>
);

const Branch = () => {
  const { branchlist, branch } = useSelector((state) => state.branchlist);
  const { BranchByUser } = useSelector((state) => state.userBranch);
  const { specialpermission } = useSelector((state) => state.amc);
  const { companyInfolist } = useSelector((state) => state.companyInfo);
  const { togglehead } = useSelector((state) => state.headerToggle);
  const [specialpermit, setspecialpermit] = useState();
  const [searchText, setSearchText] = useState("");
  const [branchId, setbranchId] = useState();
  const [sort, setSort] = useState({ key: "name", dir: 1 });
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchType());
    dispatch(fetchStateList());
    dispatch(fetchfinancialyear());
    setspecialpermit(
      specialpermission?.userSpecialPermissionList?.find(
        (e) => e.specialPermission.name == "Branch Settings"
      )
    );
  }, []);

  const handletogglehead = () => {
    dispatch(setheadertoggle(!togglehead));
  };
  const handleSearch = (e) => setSearchText(e.target.value);

  const modifiedList = useMemo(() => {
    const needle = searchText?.toLowerCase() || "";
    const list = (BranchByUser || []).filter(
      (value) =>
        value?.branch?.name?.toLowerCase().includes(needle) ||
        value?.branch?.company?.name?.toLowerCase().includes(needle) ||
        value?.branch?.address2?.toLowerCase().includes(needle) ||
        value?.branch?.phone?.toLowerCase().includes(needle) ||
        value?.branch?.email?.toLowerCase().includes(needle) ||
        value?.branch?.website?.toLowerCase().includes(needle)
    );
    return list
      .slice()
      .sort(
        (a, b) =>
          String(a.branch?.name ?? "").localeCompare(String(b.branch?.name ?? ""), undefined, {
            sensitivity: "base",
          }) * sort.dir
      );
  }, [BranchByUser, searchText, sort]);

  const selected = modifiedList.find((r) => r.branch?.guid === branchId)?.branch;

  const handleSelectBranch = (record) => {
    const id = record.branch?.guid;
    setbranchId(branchId === id ? undefined : id);
  };
  const handleGetSettings = (id) => {
    dispatch(fetchBranchSettings(id));
  };
  // Opening another branch's settings replaces the app-wide branchsettings;
  // put the signed-in branch's settings back when leaving this screen.
  const signedInGuidRef = useRef(branch?.guid);
  signedInGuidRef.current = branch?.guid;
  useEffect(() => {
    return () => {
      if (signedInGuidRef.current) dispatch(fetchBranchSettings(signedInGuidRef.current));
    };
  }, []);

  const renderRow = (r) => ({
    name: r.branch?.name,
    sub: (
      <>
        <span className="code">{r.branch?.company?.name || "—"}</span>
        {r.branch?.phone ? <span>{r.branch.phone}</span> : null}
        {r.branch?.email ? <span className="mail">{r.branch.email}</span> : null}
      </>
    ),
    right:
      r.branch?.guid === branch?.guid ? (
        <span className="axa-chip ok">Signed in</span>
      ) : (
        <span className={"axa-dot on"} />
      ),
  });

  return (
    <AdminScreen
      title="Branch"
      subtitle="The branches you may work in, and what each one is set up as"
      searchValue={searchText}
      onSearchChange={handleSearch}
      onSearchClear={() => setSearchText("")}
      searchPlaceholder="Name, company, phone, email…"
      searchHint={null}
      tools={
        <>
          <button
            type="button"
            className="axa-iconbtn"
            title="Reload"
            onClick={() => setSearchText("")}
          >
            <AxRefreshIcon />
          </button>
          <button
            type="button"
            className="axa-iconbtn"
            aria-pressed={!!togglehead}
            title="Collapse the app header"
            onClick={handletogglehead}
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
              <path d="M4 10l4-4 4 4" />
            </svg>
          </button>
        </>
      }
      list={
        <AdminList
          items={modifiedList}
          keyOf={(r) => r.branch?.guid}
          selectedKey={branchId}
          onSelect={handleSelectBranch}
          renderRow={renderRow}
          sorts={SORTS}
          sort={sort}
          onSort={() => setSort((s) => ({ key: "name", dir: -s.dir }))}
          emptyTitle="No branch matches"
          emptyHint="Try a shorter search."
        />
      }
      detail={
        selected ? (
          <div className="axa-detailform">
            <div className="axa-dhead">
              <AxAvatar name={selected.name} />
              <div className="axa-dtitle">
                <div className="t">
                  <span className="nm">{selected.name}</span>
                </div>
                <div className="s">
                  {[selected.company?.name, selected.address2, selected.phone]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </div>
              {selected.guid === branch?.guid ? <AxPill state="saved" /> : null}
            </div>

            <div className="axa-tabbody">
              <AxSectionHead>Branch</AxSectionHead>
              <div className="axa-formgrid">
                <AxField label="Name">
                  <input type="text" value={selected.name || ""} readOnly />
                </AxField>
                <AxField label="Company">
                  <input type="text" value={selected.company?.name || ""} readOnly />
                </AxField>
                <AxField label="Phone">
                  <input type="text" className="mono" value={selected.phone || ""} readOnly />
                </AxField>
                <AxField label="Email">
                  <input type="text" value={selected.email || ""} readOnly />
                </AxField>
                <AxField label="Website">
                  <input type="text" value={selected.website || ""} readOnly />
                </AxField>
              </div>

              <AxSectionHead>Address</AxSectionHead>
              <div className="axa-formgrid">
                <AxField label="Address 1">
                  <input type="text" value={selected.address1 || ""} readOnly />
                </AxField>
                <AxField label="Address 2">
                  <input type="text" value={selected.address2 || ""} readOnly />
                </AxField>
                <AxField label="Address 3">
                  <input type="text" value={selected.address3 || ""} readOnly />
                </AxField>
              </div>

              <div className="axa-emptyline">
                Branch details are maintained in the back office — this screen shows them and
                opens the branch&apos;s own settings.
              </div>
            </div>

            <div className="axa-dfoot">
              <span className="msg">
                {specialpermit?.userRole.id !== 0
                  ? "Settings covers day close, printing, sales and general options for this branch."
                  : "You do not have the Branch Settings permission."}
              </span>
              <span className="sp" />
              {specialpermit?.userRole.id !== 0 && (
                <button
                  type="button"
                  className="axa-btn axa-btn-primary"
                  data-bs-toggle="modal"
                  data-bs-target="#branch-setting"
                  onClick={() => handleGetSettings(branchId)}
                >
                  <SettingsIcon />
                  Settings
                </button>
              )}
            </div>
          </div>
        ) : (
          <AxEmptyDetail title="Pick a branch">
            Choose a branch on the left to see its details, and to open its settings.
          </AxEmptyDetail>
        )
      }
      status={
        <AxStatusKeys
          keys={[{ k: "Click", t: "Pick a branch" }]}
          right={`${modifiedList.length} of ${BranchByUser?.length || 0} branches · ${
            companyInfolist?.[0]?.name || branchlist?.[0]?.company?.name || "company"
          }`}
        />
      }
    >
      <BranchSetting editBranch={selected} />
    </AdminScreen>
  );
};

export default Branch;
